import { ScenarioKpis } from '../types';

const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;
// A plan that ends at a death ends on the first of that month, so the death itself can
// fall up to a month after the plan's last day.
const MONTH = 1 / 12;

type TimelinePerson = {
  name: string;
  ageAtStart: number;
  // Years from the start of the plan to a death the model records within it.
  diesAfter: number | null;
};

/**
 * A projected model laid out in years from the start of its plan, so the point its money
 * runs short, the household's life expectancy and any recorded death sit on one line with
 * the plan's end. Projections made before engine 0.12.0 name no members, so they have no
 * timeline until the model is run again.
 */
export type ModelTimeline = {
  start: number;
  length: number;
  // Primary member first.
  people: TimelinePerson[];
  shortfallAfter: number | null;
  lifeExpectancyAfter: number | null;
};

export const modelTimeline = (kpis: ScenarioKpis | null): ModelTimeline | null => {
  const members = kpis?.members ?? [];
  const primary = members[0];
  if (!kpis?.projectionStart || !kpis.horizonDate || !primary) {
    return null;
  }
  const start = Date.parse(kpis.projectionStart);
  const yearsTo = (date: string) => (Date.parse(date) - start) / YEAR_MS;
  const length = yearsTo(kpis.horizonDate);
  if (!(length > 0)) {
    return null;
  }
  // The shortfall and life expectancy come as the primary member's ages.
  const fromPrimaryAge = (age: number | null | undefined) =>
    age == null ? null : age - primary.ageAtStart;
  return {
    start,
    length,
    people: members.map(member => {
      const diesAfter = member.diedOn ? yearsTo(member.diedOn) : null;
      return {
        name: member.firstName,
        ageAtStart: member.ageAtStart,
        // A death before the plan starts counts from its first day; one after it ends is never reached.
        diesAfter: diesAfter != null && diesAfter <= length + MONTH ? Math.max(diesAfter, 0) : null,
      };
    }),
    shortfallAfter: fromPrimaryAge(kpis.firstShortfallAge),
    lifeExpectancyAfter: fromPrimaryAge(kpis.lifeExpectancyAge),
  };
};

/** Where a point falls along a bar spanning `scale` years, as a percentage clamped to it. */
export const positionOnScale = (scale: number, after: number): number =>
  Math.min(Math.max((after / scale) * 100, 0), 100);

// The nudge keeps an age the engine gave as a whole number from flooring to the year below.
export const ageAfter = (person: TimelinePerson, after: number): number =>
  Math.floor(person.ageAtStart + after + 1e-9);

export const yearAfter = (timeline: ModelTimeline, after: number): number =>
  new Date(timeline.start + after * YEAR_MS).getUTCFullYear();

/** "Tom 79, Anna 76": everyone still alive at a point in the plan, with their age then. */
export const agesAfter = (timeline: ModelTimeline, after: number): string =>
  timeline.people
    .filter(person => person.diesAfter == null || person.diesAfter > after)
    .map(person => `${person.name} ${ageAfter(person, after)}`)
    .join(', ');

/** The deaths a model records within its plan, earliest first. */
export const deathsWithin = (timeline: ModelTimeline): { name: string; after: number }[] =>
  timeline.people
    .flatMap(({ name, diesAfter }) => (diesAfter == null ? [] : [{ name, after: diesAfter }]))
    .sort((a, b) => a.after - b.after);

/** The death the plan ends at, once everyone in the model has a recorded death within it. */
export const endingDeath = (timeline: ModelTimeline): { name: string; after: number } | null => {
  const deaths = deathsWithin(timeline);
  return deaths.length === timeline.people.length ? deaths[deaths.length - 1] : null;
};
